import { HttpErrorResponse, HttpInterceptorFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { APP_ENVIRONMENT } from '@core/config/app-environment';
import { tap } from 'rxjs';
import { EventTrackerErrorKind, EventTrackerResult } from './event-tracker.model';
import { EventTrackerService } from './event-tracker.service';

// Regex patterns to normalize dynamic segments in URLs.
const ID_PATTERNS: { regex: RegExp; replacement: string }[] = [
  // UUIDs
  { regex: /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, replacement: '{uuid}' },
  // Numeric IDs (standalone path segments of 4+ digits)
  { regex: /\/(\d{4,})(\/|$)/g, replacement: '/{id}$2' },
  // Alphanumeric IDs that look like order IDs (e.g., v12345viv-01)
  { regex: /\/v\d+[a-z]+-\d{2}(\/|$)/gi, replacement: '/{orderId}$1' },
];

export function normalizeUrlToTemplate(url: string): string {
  try {
    const parsed = new URL(url);
    let path = parsed.pathname;
    for (const pattern of ID_PATTERNS) {
      path = path.replace(pattern.regex, pattern.replacement);
    }
    return path;
  } catch {
    let path = url.split('?')[0];
    for (const pattern of ID_PATTERNS) {
      path = path.replace(pattern.regex, pattern.replacement);
    }
    return path;
  }
}

export function extractHost(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return 'unknown';
  }
}

function extractStoreCode(pathname: string): string | undefined {
  const match = pathname.match(/^\/withdraws(?:\/mobile)?\/[^/]+\/([^/]+)/);
  return match?.[1] || undefined;
}

function extractDocumentNumber(pathname: string): string | undefined {
  const match = pathname.match(/^\/withdraws(?:\/mobile)?\/[^/]+\/[^/]+\/[^/]+\/([^/]+)/);
  return match?.[1] || undefined;
}

function maskDocumentNumber(dni: string): string {
  if (dni.length <= 3) return '***';
  return '*'.repeat(dni.length - 3) + dni.slice(-3);
}

function extractQueryParams(url: string): Record<string, string> | undefined {
  try {
    const parsed = new URL(url);
    const params: Record<string, string> = {};
    parsed.searchParams.forEach((value, key) => {
      params[key] = value;
    });
    return Object.keys(params).length > 0 ? params : undefined;
  } catch {
    return undefined;
  }
}

function extractRequestBody(req: HttpRequest<unknown>): unknown {
  if (['POST', 'PUT', 'PATCH'].includes(req.method) && req.body !== null) {
    return req.body;
  }
  return undefined;
}

function classifyErrorKind(error: HttpErrorResponse): EventTrackerErrorKind {
  if (error.status === 0) return 'network';
  if (error.status === 408 || (error as { name?: string }).name === 'TimeoutError') return 'timeout';
  if (error.status >= 400) return 'http_error';
  return 'unknown';
}

function classifySeverity(status: number, errorKind: EventTrackerErrorKind): string {
  if (status >= 500 || errorKind === 'timeout') return 'error';
  if (status >= 400 || errorKind === 'network') return 'warn';
  return 'info';
}

/** Tracks failed HTTP requests (`http_request_failed`), same payload as legacy. */
export const eventTrackerInterceptor: HttpInterceptorFn = (req, next) => {
  const tracker = inject(EventTrackerService);
  const env = inject(APP_ENVIRONMENT);

  // Skip the event tracker API itself to avoid an infinite loop.
  const trackerApi = env.API_EVENT_TRACKER;
  if (trackerApi && req.url.startsWith(trackerApi)) {
    return next(req);
  }

  // Skip static assets (relative URLs).
  if (!req.url.startsWith('http')) {
    return next(req);
  }

  const start = performance.now();
  const pathname = window.location.pathname;
  const storeCode = extractStoreCode(pathname);
  const documentNumber = extractDocumentNumber(pathname);
  const queryParams = extractQueryParams(req.urlWithParams);
  const requestBody = extractRequestBody(req);

  return next(req).pipe(
    tap({
      next: (event) => {
        if (!(event instanceof HttpResponse)) return;

        const durationMs = Math.round(performance.now() - start);
        const result: EventTrackerResult = event.status >= 200 && event.status < 400 ? 'success' : 'fail';
        if (result !== 'fail') return;

        tracker.track({
          event_type: 'http_request',
          event_name: 'http_request_failed',
          result,
          duration_ms: durationMs,
          status_code: event.status,
          error_kind: 'none',
          store_id: storeCode,
          payload: {
            method: req.method,
            url_host: extractHost(req.url),
            route_template: normalizeUrlToTemplate(req.url),
            query_params: queryParams,
            http_status: event.status,
            response_size_bytes: event.body ? JSON.stringify(event.body).length : 0,
            ...(requestBody !== undefined && { request_body: requestBody }),
            ...(documentNumber && { dni: maskDocumentNumber(documentNumber) }),
          },
        });
      },
      error: (error: HttpErrorResponse) => {
        const durationMs = Math.round(performance.now() - start);
        const errorKind = classifyErrorKind(error);

        tracker.track({
          event_type: 'http_request',
          event_name: 'http_request_failed',
          result: 'fail',
          duration_ms: durationMs,
          status_code: error.status,
          error_kind: errorKind,
          store_id: storeCode,
          payload: {
            method: req.method,
            url_host: extractHost(req.url),
            route_template: normalizeUrlToTemplate(req.url),
            query_params: queryParams,
            http_status: error.status,
            error_message: error.message?.substring(0, 200),
            severity: classifySeverity(error.status, errorKind),
            ...(requestBody !== undefined && { request_body: requestBody }),
            ...(documentNumber && { dni: maskDocumentNumber(documentNumber) }),
          },
        });
      },
    }),
  );
};
